/**
 * OutOfPocketBand — the day's out-of-pocket maximum, rendered in ONE place.
 *
 * **Why this is a module and not inline JSX.** Three states have to stay
 * distinct and two cards have to render them: `SameDayVisitsCard` (a date of
 * service with more than one visit) and `EstimatedResponsibility` (a single
 * visit — most of the live volume). A second copy of this logic is how the
 * states get collapsed back together, and every way of collapsing them tells a
 * patient something false:
 *
 *   - `oop_remaining_minor_units === null` — **UNKNOWN.** The payer returned no
 *     out-of-pocket figure, or this is a self-pay / cash patient. No cap was
 *     applied. **Render nothing.** `$0.00` here reads as "I have met my
 *     maximum, I owe nothing" — the exact opposite of the truth for a cash
 *     patient, who owes the full amount.
 *   - `oop_met` — **MET**, known and `<= 0`. The plan pays 100% of covered
 *     services and the day owes `$0`. A success alert.
 *   - `oop_capped` — **PARTIALLY CAPPED** (BFF 0.3.0 / appointment-api 0.8.0).
 *     The maximum reduced the day's total. Without the notice the card shows a
 *     deductible plus a copay or coinsurance, then asks for a smaller number,
 *     and nothing on the page accounts for the difference.
 *
 * `oop_met` and `oop_capped` are **never both true** — the backend guarantees
 * it, and `CappedNotice` is behind an `else` here so a backend bug cannot print
 * both. `oop_capped` is also false when the computed total happened to equal
 * the remaining maximum exactly: nothing was reduced, so claiming "we limited
 * this" would be a lie about a correct figure.
 *
 * Nothing here is derived from the lines. The server allocates the maximum
 * across the day in start-time order and `day_owed_minor_units` is already the
 * capped figure; this component only explains it.
 */
import { Alert } from '@tensaw/design-system/feedback';

import type { DayAccumulators } from '../../api/types';
import { format } from '../../lib/money';

/**
 * The "Max out-of-pocket remaining" figure row, or `null` when there is none to
 * show. Returned as data rather than JSX so `SameDayVisitsCard` can lay it out
 * in the same `<dl>` as its deductible rows.
 *
 * Hidden when the maximum is unknown (`null`) AND when it is met — the met case
 * gets the alert instead, where "$0.00 remaining" would be redundant next to
 * "Max out-of-pocket met".
 */
export function outOfPocketFigure(
  day: DayAccumulators,
): { label: string; value: string } | null {
  const oop = day.oop_remaining_minor_units;
  if (oop === null || day.oop_met) return null;
  return { label: 'Max out-of-pocket remaining', value: format(oop) };
}

/** True when `OutOfPocketNotices` would render something. */
export function hasOutOfPocketNotice(day: DayAccumulators): boolean {
  return day.oop_met || day.oop_capped === true;
}

/**
 * The met alert or the capped notice — never both, never neither-but-empty.
 *
 * `className` is applied to the first rendered element so a caller can space it
 * off the rows above it.
 */
export function OutOfPocketNotices({
  day,
  className,
}: {
  /** Nullable so callers can pass `dayAccumulators` straight through. */
  day: DayAccumulators | null;
  className?: string;
}) {
  if (day === null) return null;
  if (day.oop_met) {
    return (
      <Alert variant="success" title="Max out-of-pocket met" className={className}>
        Your plan pays 100% of covered services for the rest of your plan year.
      </Alert>
    );
  }
  // Deliberately an `else`: see the module docstring. `=== true` because the
  // field is optional — an older BFF omits it, which is "no cap reported".
  if (day.oop_capped === true) {
    return <CappedNotice day={day} className={className} />;
  }
  return null;
}

/**
 * "You have reached your out-of-pocket maximum for this visit."
 *
 * The amount quoted is `oop_remaining_minor_units` — what was left of the
 * maximum before today, which is exactly what the patient is being charged
 * (the backend guarantees `oop_applied === oop_remaining` whenever the day is
 * capped). `oop_applied_minor_units` is preferred when present because it is
 * the figure that was actually consumed; the fallback keeps the sentence
 * complete against a BFF that sends `oop_capped` without it.
 */
function CappedNotice({
  day,
  className,
}: {
  day: DayAccumulators;
  className?: string;
}) {
  const applied = day.oop_applied_minor_units ?? day.oop_remaining_minor_units;
  return (
    <Alert variant="info" title="Limited by your out-of-pocket maximum" className={className}>
      {applied === null ? (
        <>
          Your estimated share for today is more than what was left of your
          out-of-pocket maximum, so you are only being asked for the remainder.
        </>
      ) : (
        <>
          Your estimated share for today is more than the {format(applied)} left
          on your out-of-pocket maximum, so that is all you are being asked to
          pay. Your plan covers the rest.
        </>
      )}
    </Alert>
  );
}

/**
 * The whole band — figure row plus notices — in the muted styling both cards
 * use. `EstimatedResponsibility` renders this; `SameDayVisitsCard` composes the
 * two exports above into its existing day band instead, so the day's
 * deductible and out-of-pocket figures stay on one line.
 *
 * Returns `null` when there is nothing to say, so a single-visit card on a
 * patient with no out-of-pocket figure is byte-identical to before.
 */
export function OutOfPocketBand({ day }: { day: DayAccumulators | null }) {
  if (day === null) return null;
  const figure = outOfPocketFigure(day);
  if (figure === null && !hasOutOfPocketNotice(day)) return null;
  return (
    <div className="border-b border-border bg-muted/20 px-4 py-4 sm:px-6">
      {figure !== null && (
        <dl className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
          <div className="flex items-baseline gap-2">
            <dt className="text-muted-foreground">{figure.label}</dt>
            <dd className="font-medium text-foreground tabular-nums">{figure.value}</dd>
          </div>
        </dl>
      )}
      <OutOfPocketNotices day={day} className={figure !== null ? 'mt-3' : undefined} />
    </div>
  );
}
